// ============================================================
// devis-pdf.service.ts
// Service de génération PDF du devis — charte Capgemini (bleu)
// Ordre : Contexte et objectif → Expression du besoin → Chiffrage
// ============================================================

import { Injectable } from '@angular/core';
import { DevisStatut, IDevisResponse } from 'src/app/shared/models/devis';


@Injectable({
  providedIn: 'root'
})
export class DevisPdfService {

  // --- Palette Capgemini ---
  private readonly BLUE = '#0070AD';        // bleu principal Capgemini
  private readonly BLUE_DARK = '#00538A';   // bleu foncé (accents)
  private readonly BLUE_LIGHT = '#12ABDB';  // bleu clair (spade du logo)
  private readonly GREY = '#4D4F53';
  private readonly LIGHT_GREY = '#9B9B9B';

  /**
   * Génère et télécharge le PDF du devis
   * @param dateEnvoiSncf date ISO du dernier envoi à la SNCF (CREE -> EN_ATTENTE),
   *                       récupérée via GET /devis/{id}/date-envoi-sncf. null si jamais envoyé.
   */


  private pdfMakeInstance: any;

  private async getPdfMake(): Promise<any> {

    if (this.pdfMakeInstance) {
      console.log('[getPdfMake] instance déjà en cache');
      return this.pdfMakeInstance;
    }

    console.log('[getPdfMake] import de pdfmake/build/pdfmake...');
    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    console.log('[getPdfMake] pdfmake importé', pdfMakeModule);

    console.log('[getPdfMake] import de pdfmake/build/vfs_fonts...');
    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    console.log('[getPdfMake] vfs_fonts importé', pdfFontsModule);

    const pdfMake =
      (pdfMakeModule as any).default || pdfMakeModule;

    const fonts = pdfFontsModule as any;
    const vfs = fonts.default?.pdfMake?.vfs
      || fonts.pdfMake?.vfs
      || fonts.default?.vfs
      || fonts.vfs
      || fonts.default
      || fonts;

    if (!vfs || !vfs['Roboto-Medium.ttf']) {
      throw new Error('pdfmake : les polices Roboto sont absentes de la VFS.');
    }

    pdfMake.vfs = vfs;

    this.pdfMakeInstance = pdfMake;

    return pdfMake;
  }



 async download(
  devis: IDevisResponse,
  dateEnvoiSncf?: string | null
): Promise<void> {

  const logo = await this.getBase64ImageFromURL(
    'assets/logo-capgemini.png'
  );

  const docDefinition = this.buildDocDefinition(
    devis,
    logo,
    dateEnvoiSncf ?? null
  );

  const pdfMake = await this.getPdfMake();

  pdfMake
    .createPdf(docDefinition)
    .download(
      `Devis-${devis.devisReference ?? devis.devisId}.pdf`
    );
}

  /**
   * Génère le PDF du devis et renvoie ses octets bruts, sans déclencher de
   * téléchargement — utilisé pour fusionner ce PDF avec d'autres documents
   * (CV des collaborateurs) avant le téléchargement final.
   */
  async generateBytes(
    devis: IDevisResponse,
    dateEnvoiSncf?: string | null
  ): Promise<Uint8Array> {

    console.log('[generateBytes] début, chargement du logo...');

    const logo = await this.getBase64ImageFromURL(
      'assets/logo-capgemini.png'
    );

    console.log('[generateBytes] logo chargé ?', !!logo, 'longueur =', logo?.length);

    const docDefinition = this.buildDocDefinition(
      devis,
      logo,
      dateEnvoiSncf ?? null
    );

    console.log('[generateBytes] docDefinition construit');

    const pdfMake = await this.getPdfMake();
    console.log('[generateBytes] pdfMake prêt, appel de createPdf...');

    const pdfDoc = pdfMake.createPdf(docDefinition);

    console.log('[DevisPdfService] pdfDoc créé, méthodes disponibles :', Object.keys(pdfDoc));

    // getBuffer() existe en API callback (anciennes versions de pdfmake) ou
    // en API Promise (versions récentes) selon la version installée.
    if (typeof pdfDoc.getBuffer !== 'function') {
      throw new Error('pdfmake : la méthode getBuffer() est indisponible sur cette version.');
    }

    return new Promise<Uint8Array>((resolve, reject) => {

      const timeout = setTimeout(() => {
        reject(new Error('Génération du PDF du devis : délai dépassé (getBuffer n\'a jamais répondu).'));
      }, 15000);

      try {
        const maybePromise = pdfDoc.getBuffer((buffer: Uint8Array) => {
          clearTimeout(timeout);
          console.log('[DevisPdfService] getBuffer callback reçu, taille =', buffer?.length);
          resolve(buffer);
        });

        // Versions récentes de pdfmake : getBuffer() renvoie directement une Promise
        // et n'appelle jamais le callback.
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise
            .then((buffer: Uint8Array) => {
              clearTimeout(timeout);
              console.log('[DevisPdfService] getBuffer promise résolue, taille =', buffer?.length);
              resolve(buffer);
            })
            .catch((err: any) => {
              clearTimeout(timeout);
              reject(err);
            });
        }
      } catch (err) {
        clearTimeout(timeout);
        reject(err);
      }
    });
  }

  // ============================================================
  // Construction du document
  // ============================================================

  private buildDocDefinition(devis: IDevisResponse, logo: string, dateEnvoiSncf: string | null): any {

    const activites = (devis.febActivites ?? []).map(a => [
      { text: a.label, fontSize: 9, margin: [6, 5, 6, 5] },
      { text: `${a.pourcentage}%`, fontSize: 9, bold: true, alignment: 'center', margin: [0, 5, 0, 5] }
    ]);

    return {

      pageMargins: [36, 36, 36, 40],
      defaultStyle: { fontSize: 9, color: this.GREY },

      styles: {
        h1: { fontSize: 24, bold: true, color: this.BLUE, characterSpacing: 0.5 },
        sectionTitle: { fontSize: 12, bold: true, color: this.BLUE, margin: [0, 18, 0, 6] },
        tableHeader: { fontSize: 8, bold: true, color: 'white', margin: [6, 5, 6, 5] },
        label: { fontSize: 7.5, bold: true, color: this.LIGHT_GREY },
        value: { fontSize: 10, bold: true, color: this.GREY }
      },

      content: [

        // ===== HEADER : référence du devis + logo Capgemini =====
        {
          columns: [
            {
              width: '*',
              text: devis.devisReference ?? 'DEVIS',   // la ref contient déjà "DEVIS-"
              style: 'h1',
              margin: [0, 6, 0, 0]    // centre le texte sur la hauteur du logo
            },
            {
              image: logo,
              width: 110,
              alignment: 'right',
              margin: [0, 2, 0, 0]
            }
          ],
          columnGap: 10,
          margin: [0, 0, 0, 16]
        },

        // ===== ENCADRÉ : PROJET > BÉNÉFICIAIRE =====
        {
          table: {
            widths: ['*'],
            body: [[
              {
                text: `${(devis.febProjectNom ?? 'PROJET').toUpperCase()}  >  ${(devis.devisBeneficiaire ?? 'BÉNÉFICIAIRE').toUpperCase()}`,
                color: this.BLUE,
                bold: true,
                fontSize: 11,
                margin: [8, 8, 8, 8]
              }
            ]]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => this.BLUE,
            vLineColor: () => this.BLUE
          },
          margin: [0, 0, 0, 12]
        },

        // ===== Bloc identité =====
        {
          columns: [
            {
              width: '40%',
              stack: [
                { text: [{ text: 'Référence :   ', style: 'label' }, { text: devis.devisReference ?? '-', style: 'value' }] },
                { text: [{ text: 'Bénéficiaire : ', style: 'label' }, { text: devis.devisBeneficiaire ?? '-', style: 'value' }], margin: [0, 3, 0, 0] },
                { text: [{ text: 'Division :       ', style: 'label' }, { text: devis.febDivisionDemandeurNom ?? '-', fontSize: 9 }], margin: [0, 3, 0, 0] }
              ]
            },
            {
              width: '*',
              stack: [
                { text: [{ text: 'Statut : ', style: 'label' }, { text: devis.devisStatut, bold: true, color: this.getStatutColor(devis.devisStatut) }] },
                { text: [{ text: 'Créé le : ', style: 'label' }, { text: this.formatDate(devis.devisCreatedAt), fontSize: 9 }], margin: [0, 3, 0, 0] }
              ]
            }
          ],
          margin: [4, 0, 0, 14]
        },

        // ===== 1. CONTEXTE ET OBJECTIF =====
        { text: 'CONTEXTE ET OBJECTIF', style: 'sectionTitle' },
        this.justifiedText(devis.febContexteObjectifProjet),

        // ===== 2. EXPRESSION DU BESOIN =====
        { text: 'EXPRESSION DU BESOIN', style: 'sectionTitle' },
        this.febInfoGrid(devis),

        // ----- Répartition des activités (rattachée au besoin) -----
        {
          table: {
            widths: ['*', 100],
            headerRows: 1,
            body: [
              [
                { text: 'ACTIVITÉ', style: 'tableHeader', fillColor: this.BLUE },
                { text: 'RÉPARTITION', style: 'tableHeader', fillColor: this.BLUE_DARK, alignment: 'center' }
              ],
              ...activites
            ]
          },
          layout: this.greyGrid(),
          margin: [0, 8, 0, 0]
        },

        // ===== 3. CHIFFRAGE (nouvelle page) =====
        { text: 'CHIFFRAGE', style: 'sectionTitle', pageBreak: 'before' },
        {
          text: 'Tous les tarifs sont exprimés en euros hors taxes (HT).',
          fontSize: 7.5,
          italics: true,
          color: this.LIGHT_GREY,
          margin: [0, 0, 0, 4]
        },
        this.chiffrageGrid(devis),

        // Remarque si le devis n'est pas encore validé (pas de date de fin de travaux réelle)
        ...((devis as any).devisDateFinTravaux ? [] : [{
          text: '* Devis non encore validé : la date de fin affichée est la date de fin prévisionnelle.',
          fontSize: 7.5,
          italics: true,
          color: this.BLUE_DARK,
          margin: [0, 4, 0, 0]
        }]),

        // ===== MONTANT TOTAL =====
        {
          margin: [0, 14, 0, 6],
          table: {
            widths: ['*'],
            body: [[
              {
                stack: [
                  { text: 'MONTANT TOTAL PRÉVISIONNEL (HT)', alignment: 'center', bold: true, fontSize: 10, color: this.BLUE_DARK },
                  { text: this.formatMontant(devis.devisMontantTotal), alignment: 'center', bold: true, fontSize: 22, color: this.BLUE, margin: [0, 6, 0, 0] }
                ],
                fillColor: '#EAF4FA',
                margin: [0, 12, 0, 12]
              }
            ]]
          },
          layout: 'noBorders'
        },

        // ===== FOOTER 2 colonnes =====
        {
          columns: [
            {
              width: '48%',
              stack: [
                { text: 'LA GARANTIE PACKGEMINI', color: this.BLUE, bold: true, fontSize: 11, margin: [0, 24, 0, 6] },
                {
                  text: 'Ce devis est établi conformément au processus de chiffrage validé entre Capgemini et la SNCF. '
                    + 'Il engage les deux parties après acceptation et ne peut être modifié que via un retour arrière tracé.',
                  fontSize: 7.5, lineHeight: 1.3
                }
              ]
            },
            { width: '4%', text: '' },
            {
              width: '48%',
              stack: [
                { text: 'CONDITIONS D\'UTILISATION DU DEVIS', color: this.BLUE, bold: true, fontSize: 11, margin: [0, 24, 0, 6] },
                { text: 'NOMINATIF, PERSONNEL ET INCESSIBLE.', bold: true, fontSize: 7.5, color: this.BLUE_DARK, margin: [0, 0, 0, 3] },
                {
                  text: 'Valable uniquement pour le projet, la division et le périmètre désignés. '
                    + 'Toute modification du chiffrage nécessite une nouvelle validation. '
                    + this.getMentionValidite(dateEnvoiSncf),
                  fontSize: 7.5, lineHeight: 1.3
                }
              ]
            }
          ]
        },

        // ===== Bandeau info (fin de document) =====
        {
          text: [
            { text: 'Document généré automatiquement le ' + new Date().toLocaleDateString('fr-FR') + ' : ', bold: true },
            'ce devis récapitule le chiffrage établi à partir de la fiche d\'expression de besoin. '
            + 'Il est valable sous réserve d\'acceptation par la SNCF. ' + this.getMentionValidite(dateEnvoiSncf)
          ],
          fontSize: 8,
          italics: true,
          color: this.LIGHT_GREY,
          margin: [0, 16, 0, 0]
        }
      ]
    };
  }

  // ============================================================
  // Helpers privés
  // ============================================================

  /**
   * Grille CHIFFRAGE (2 champs par ligne)
   * PRESTATION MAJORÉE   | UO / ACTIVITÉ
   * PRIX UO PROPOSÉ      | PRIX UO GRILLE
   * DATE DÉMARRAGE       | DATE FIN DE PRESTATION
   * DURÉE PRÉVISIONNELLE | PROVISION FRAIS
   * DATE D'ENTRETIEN     |
   */
  private chiffrageGrid(devis: IDevisResponse): any {

    const d = devis as any; // adapte les noms si besoin selon ton IDevisResponse

    const fields: { label: string; value: any }[] = [
      { label: 'PRESTATION MAJORÉE', value: d.devisPrestationMajoree === true ? 'Oui' : d.devisPrestationMajoree === false ? 'Non' : d.devisPrestationMajoree },
      { label: 'UO / ACTIVITÉ', value: devis.febUoActivitePressentiLabel ?? null },
      // Prix UO proposé affiché uniquement s'il diffère du prix grille
      ...(devis.devisPrixUoPropose != null && devis.devisPrixUoPropose !== d.devisPrixUoGrille
        ? [{ label: 'PRIX UO PROPOSÉ', value: `${this.formatMontant(devis.devisPrixUoPropose).replace(' €', '')} €/j` }]
        : []),
      { label: 'PRIX UO GRILLE', value: d.devisPrixUoGrille != null ? `${this.formatMontant(d.devisPrixUoGrille).replace(' €', '')} €/j` : d.devisPrixUoGrille },
      { label: 'DATE DÉMARRAGE', value: this.formatDate(devis.devisDateDemarrageGo) },
      {
        label: d.devisDateFinTravaux ? 'DATE FIN DE PRESTATION' : 'DATE FIN PRÉVISIONNELLE',
        value: d.devisDateFinTravaux
          ? this.formatDate(d.devisDateFinTravaux)
          : `${this.formatDate(d.devisdDateFinPrevisionnelle)} *`
      },
      { label: 'DURÉE PRÉVISIONNELLE', value: devis.devisDuree != null ? `${devis.devisDuree} j` : null },
      { label: 'PROVISION FRAIS', value: this.formatMontant(devis.devisProvisionFrais) },
      { label: 'DATE D\'ENTRETIEN', value: this.formatDate(d.dateEntretien) }
    ];

    return this.infoGrid(fields, 2);
  }

  /**
   * Grille des informations de la FEB (3 champs par ligne).
   * Ajoute simplement une entrée { label, value } pour afficher un champ de plus.
   */
  private febInfoGrid(devis: IDevisResponse): any {

    const d = devis as any; // tolère les variantes de nommage (febProjectNom / febProjetNom)

    const fields: { label: string; value: any }[] = [
      { label: 'PROJET', value: d.febProjectNom ?? d.febProjetNom },
      { label: 'LIBELLÉ FEB', value: devis.febLabel },
      { label: 'DIVISION DEMANDEUSE', value: devis.febDivisionDemandeurNom },
      { label: 'LOCALISATION', value: devis.febLocalisationLabel },
      { label: 'UO / ACTIVITÉ PRESSENTIE', value: devis.febUoActivitePressentiLabel },
      { label: 'BÉNÉFICIAIRE', value: devis.devisBeneficiaire },
      { label: 'STATUT FEB', value: d.febStatut }
      // 👉 ajoute ici d'autres champs FEB si ton IDevisResponse en expose plus
    ];

    return this.infoGrid(fields, 3);
  }

  /**
   * Grille générique label/valeur, style "fiche" :
   * petits labels gris en majuscules, valeurs en dessous,
   * fine ligne de séparation entre chaque rangée.
   */
  private infoGrid(fields: { label: string; value: any }[], perRow: number): any {

    // On enlève les champs totalement absents du modèle (undefined),
    // mais on garde ceux à null pour afficher '—'
    const visible = fields.filter(f => f.value !== undefined);

    const body: any[] = [];

    for (let i = 0; i < visible.length; i += perRow) {

      const chunk = visible.slice(i, i + perRow);

      // Ligne de labels
      body.push(chunk.map(f => ({
        text: f.label,
        style: 'label',
        margin: [6, 7, 6, 0]
      })).concat(Array(perRow - chunk.length).fill({ text: '' })));

      // Ligne de valeurs
      body.push(chunk.map(f => ({
        text: (f.value ?? '—').toString(),
        fontSize: 10,
        bold: true,
        margin: [6, 1, 6, 7]
      })).concat(Array(perRow - chunk.length).fill({ text: '' })));
    }

    return {
      table: {
        widths: Array(perRow).fill(`${100 / perRow}%`),
        body
      },
      layout: {
        // trait fin uniquement entre les paires label/valeur
        hLineWidth: (i: number) => (i % 2 === 0 && i !== 0 && i !== body.length ? 0.7 : 0),
        vLineWidth: () => 0,
        hLineColor: () => '#E5E5E5'
      }
    };
  }

  /**
   * Découpe un texte multi-lignes en paragraphes justifiés.
   * pdfMake ne justifie pas les lignes terminées par \n, donc on
   * transforme chaque ligne en paragraphe indépendant.
   */
  private justifiedText(text?: string | null): any {
    if (!text) {
      return { text: '-', fontSize: 9 };
    }

    const paragraphs = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => ({
        text: line,
        fontSize: 9,
        lineHeight: 1.25,
        alignment: 'justify',
        margin: [0, 0, 0, 6]   // espace entre paragraphes
      }));

    return { stack: paragraphs };
  }

  /**
   * Phrase de validité du devis.
   * - Si le devis a déjà été envoyé à la SNCF (audit trouvé) : validité = date d'envoi + 1 mois
   * - Sinon (pas encore envoyé) : validité annoncée à compter de la date d'émission du PDF
   */
  private getMentionValidite(dateEnvoiSncf: string | null): string {

    if (dateEnvoiSncf) {
      const expiration = this.addOneMonth(new Date(dateEnvoiSncf));
      return `Devis envoyé à la SNCF le ${this.formatDate(dateEnvoiSncf)}, `
        + `valable jusqu'au ${expiration}.`;
    }

    const expiration = this.addOneMonth(new Date());
    return `Devis non encore envoyé à la SNCF : à compter de son envoi, `
      + `il sera valable 1 mois (soit jusqu'au ${expiration} en cas d'envoi aujourd'hui).`;
  }

  private addOneMonth(date: Date): string {
    const d = new Date(date);
    d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString('fr-FR');
  }

  private greyGrid(): any {
    return {
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      hLineColor: () => '#CCCCCC',
      vLineColor: () => '#CCCCCC'
    };
  }

  private getBase64ImageFromURL(url: string): Promise<string> {
    console.log('[getBase64ImageFromURL] chargement de', url);
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Pas de crossOrigin ici : l'image vient du même serveur (assets/),
      // et le forcer déclenchait une requête CORS qui "tachait" le canvas
      // (l'asset local n'envoie pas d'en-tête Access-Control-Allow-Origin),
      // ce qui faisait échouer toDataURL() silencieusement dans onload,
      // sans jamais résoudre ni rejeter la Promise (blocage permanent).
      img.onload = () => {
        console.log('[getBase64ImageFromURL] image chargée, taille =', img.width, 'x', img.height);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas non disponible'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          console.log('[getBase64ImageFromURL] toDataURL OK, longueur =', dataUrl.length);
          resolve(dataUrl);
        } catch (err) {
          console.error('[getBase64ImageFromURL] ERREUR dans toDataURL', err);
          reject(err);
        }
      };
      img.onerror = (error) => {
        console.error('[getBase64ImageFromURL] ERREUR de chargement de l\'image', error);
        reject(new Error(`Impossible de charger le logo (${url})`));
      };
      img.src = url;
    });
  }

  private formatDate(date?: string | null): string {
    if (!date) {
      return '-';
    }
    return new Date(date).toLocaleDateString('fr-FR');
  }

  private formatMontant(value?: number | null): string {

    const montant = value ?? 0;

    return montant
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      + ' €';
  }

  private getStatutColor(statut: DevisStatut): string {
    switch (statut) {
      case DevisStatut.ACCEPTE:
        return '#2E7D32';
      case DevisStatut.REFUSE:
        return '#C62828';
      case DevisStatut.EN_ATTENTE:
        return '#F57C00';
      case DevisStatut.ARCHIVE:
        return '#616161';
      default:
        return '#1565C0';
    }
  }
}